import { inject, Injectable } from '@angular/core';
import { VersionStateType } from '../../../../models/ts/version-state-type.model';
import { CollectionInstanceCompareDto } from '../../../../models/ts/collection-instance-compare-dto.model';
import { InstanceComparisonModalModel } from '../../../shared/components/modals/data-collection/compare-base-modal/compare-modal-model';
import {
  FileUploadEditOnlineModalComponentData,
  FileUploadEditOnlineModalComponentMode
} from '../../../../models/ts/file-upload-edit-online-modal-component-mode';
import { FileUploadEditOnlineModalComponent } from '../../../shared/components/file-upload/file-upload/file-upload-edit-online-modal/file-upload-edit-online-modal.component';
import { finalize, take, tap } from 'rxjs';
import { GridService } from '../../../features/bizzmine/widgets/collection-list-widget/services/grid/grid.service';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { DownloadService } from '../download/download.service';
import { EditOnlineApiService } from '../../../api/bizzmine/edit-online/edit-online-api.service';
import { HttpResponse } from '@angular/common/http';
import { FileUploadService } from './file-upload.service';
import { FileInfoModel } from '../../../../models/ts/file-info-model.model';
import { CompareModalComponent } from '../../../shared/components/modals/data-collection/compare-base-modal/variants/compare-modal/compare-modal.component';
import { CompareModalType } from '../../../shared/components/modals/data-collection/compare-base-modal/compare-base-modal.component';

@Injectable({
  providedIn: 'root'
})
export class EditOnlineService {
  private gridservice = inject(GridService);
  private dialog = inject(Dialog);
  private editOnlineApiService = inject(EditOnlineApiService);
  private fileUploadService = inject(FileUploadService);

  /**
   * Opens the view/edit online modal - checks for draft (with compare) and or DocumentSatus.
   * @param {{CollectionsID: number, ID: number, VersionsID: number, DraftsID: number, CanReadDrafts: boolean, DocumentStatus: VersionStateType}} dataItem
   * @param {FileUploadEditOnlineModalComponentMode} mode View or Edit file
   */
  public openOnline(dataItem: {
    CollectionsID: number,
    ID: number,
    VersionsID: number,
    DraftsID: number,
    CanReadDrafts: boolean,
    DocumentStatus: VersionStateType
  }, mode: FileUploadEditOnlineModalComponentMode = FileUploadEditOnlineModalComponentMode.View): void {
    if (dataItem.CanReadDrafts && dataItem.DraftsID != null && dataItem.DraftsID > 0) {
      this.openViewOnlineWithoutDraftValidation({
        ID: dataItem.ID,
        VersionsID: dataItem.DraftsID ?? dataItem.VersionsID,
        CollectionsID: dataItem.CollectionsID
      }, mode);
    } else if (!dataItem.CanReadDrafts || (dataItem.DraftsID == null || dataItem.DraftsID == 0) ||
      (dataItem.DocumentStatus == null || dataItem.DocumentStatus == VersionStateType.Active)) {
      this.openViewOnlineWithoutDraftValidation({
        ID: dataItem.ID,
        VersionsID: dataItem.VersionsID,
        CollectionsID: dataItem.CollectionsID
      }, mode);
    } else {
      throw new Error('Cannot view Instance online (no edit permission and no public version)');
    }
  }

  /**
   * Opens the view online modal without checking if the instance has a draft version.
   * @param {{CollectionsID: number, ID: number, VersionsID: number}} dataItem
   * @param {FileUploadEditOnlineModalComponentMode} mode View or Edit file
   */
  public openViewOnlineWithoutDraftValidation(dataItem: {
    CollectionsID: number,
    ID: number,
    VersionsID: number
  }, mode: FileUploadEditOnlineModalComponentMode): void {
    const params: FileUploadEditOnlineModalComponentData = {
      mode: mode,
      docheckoutcheckin: true,
      fromGrid: {
        collectionId: dataItem.CollectionsID,
        instanceId: dataItem.ID,
        versionId: dataItem.VersionsID
      },
      mediaId: 0,
      filename: '',
      modificationreason: '',
      closeModalMethod: (): void => {
        dlgRef.close();
      },
      finishMethod: (data: any): void => {
        if (data.openedOneDriveWindow) {
          data.openedOneDriveWindow.close();
        }
        //This function should only be called in edit mode but as an extra check:
        if (mode == FileUploadEditOnlineModalComponentMode.Edit)
          this.uploadFileAfterEditOnline(data, {
            collectionsId: dataItem.CollectionsID,
            instanceId: dataItem.ID,
            versionId: dataItem.VersionsID
          }, dlgRef);
        else dlgRef.close();
      }
    };
    const dlgRef = this.dialog.open(FileUploadEditOnlineModalComponent, {
      data: params,
      disableClose: true
    });
    dlgRef.closed.pipe(take(1)).subscribe();
  }


  /**
   * Uploads the file after editing it online.
   * @param uploadData {modificationReasonFromOnlineEdit, mediaId, oneDriveKey, lastModified, fileInfo}
   * @param dataItem {collectionsId, instanceId, versionId}
   * @param {DialogRef} dlgRef to close the modal after the upload
   * @private
   */
  private uploadFileAfterEditOnline(uploadData: {
                                      modificationReasonFromOnlineEdit: string,
                                      mediaId: number,
                                      oneDriveKey: string,
                                      lastModified: string,
                                      fileInfo: FileInfoModel
                                    } | null,
                                    dataItem: { collectionsId: number, instanceId: number, versionId: number } | null,
                                    dlgRef: DialogRef<unknown, FileUploadEditOnlineModalComponent>): void {
    if (uploadData == null || dataItem == null ||
      uploadData.mediaId == 0 || uploadData.oneDriveKey == null || uploadData.lastModified == null) {
      console.error(`Can't upload file after edit online. Invalid parameters:
       uploadData: ${uploadData}, dataItem: ${dataItem}`);
      return;
    }
    this.editOnlineApiService
      .retrieveOnlineMediaId(uploadData)
      .pipe(
        take(1),
        tap((response) => {
          if (response.body != null && response.body.byteLength > 0) {
            const file = this.createFileFromResponse(response);
            this.fileUploadService
              .uploadFile(file, uploadData.fileInfo.FileName, uploadData.fileInfo.Extension, {
                collectionId: dataItem.collectionsId,
                instanceId: dataItem.instanceId,
                versionId: dataItem.versionId,
                modificationReason: uploadData.modificationReasonFromOnlineEdit
              })
              .then(() => {
                this.editOnlineApiService
                  .removeCheckOutOnlineMedia({
                    mode: FileUploadEditOnlineModalComponentMode.Edit,
                    oneDriveKey: uploadData.oneDriveKey,
                    undoCheckout: false,
                    mediasId: uploadData.mediaId
                  })
                  .pipe(
                    take(1),
                    finalize(() => dlgRef.close())
                  )
                  .subscribe();
              })
              .catch((err) => {
                console.error(err);
              });
          } else {
            this.editOnlineApiService
              .removeCheckOutOnlineMedia({
                mode: FileUploadEditOnlineModalComponentMode.Edit,
                oneDriveKey: uploadData.oneDriveKey,
                undoCheckout: true,
                mediasId: uploadData.mediaId
              })
              .pipe(
                take(1),
                finalize(() => dlgRef.close())
              )
              .subscribe();
          }
        })
      )
      .subscribe();
  }

  private createFileFromResponse(response: HttpResponse<ArrayBuffer>): File {
    if (response.body == null || response.body.byteLength == 0) {
      throw new Error('No file data found in response');
    }
    const headers = response.headers;
    const fileName = DownloadService.cleanupFileName(headers.get('x-filename'));
    const contentType = headers.get('contentType') ?? '';
    return new File([response.body], fileName, { type: contentType });
  }
}
